import { createSlice } from '@reduxjs/toolkit';
import { clearUsersState } from '~/store/users/slices.ts';
import { login, loginManager, logout } from '~/store/users/thunks.ts';
import {
  resetGuaranteesApplicationsAction,
  setGuaranteesFulfilledAction,
  setGuaranteesPendingAction,
  setGuaranteesRejectedAction,
} from './actions/actions.ts';
import { initialState } from './initialState.ts';
import {
  handleFetchGuaranteesAccountsPending,
  handleFetchGuaranteesAccountsRejected,
  handleFetchGuaranteesApplicationFulfilled,
  handleFetchGuaranteesApplicationPending,
  handleFetchGuaranteesMainDealsPending,
  handleFetchGuaranteesMainDealsRejected,
  handlePatchGuaranteesBaseRelationsFulfilled,
  handlePatchGuaranteesBeneficiaryFulfilled,
  synchronizeGuaranteesBeneficiaryResponse,
  handlePatchGuaranteesExecutionFormFulfilled,
  handlePatchGuaranteesFormFulfilled,
  handlePatchGuaranteesIssueGuaranteeFulfilled,
  handlePatchGuaranteesMainDealConditionsFulfilled,
  handlePatchGuaranteesManagerTermsFulfilled,
  handlePatchGuaranteesPrincipalFulfilled,
  resetSessionScopedGuaranteesState,
} from './reducers.ts';
import {
  createGuaranteesApplication,
  checkGuaranteesApplicationRequest,
  fetchGuaranteesApplication,
  fetchGuaranteesApplicationTimeline,
  fetchGuaranteesApplications,
  fetchGuaranteesApplicationTenderInfo,
  fetchGuaranteesAccounts,
  fetchGuaranteesAdministrationSupervisorInfo,
  fetchGuaranteesAdministrationRoles,
  fetchGuaranteesAdministrationTasks,
  fetchGuaranteesTaskSupervisors,
  fetchGuaranteesApprovalComment,
  fetchGuaranteesAmTemplateDocument,
  fetchGuaranteesBaseRelations,
  fetchGuaranteesBeneficiary,
  fetchGuaranteesConclusionTemplateDocument,
  fetchGuaranteesExecutionForm,
  fetchGuaranteesForm,
  fetchGuaranteesMainDealConditions,
  fetchGuaranteesMainDeals,
  fetchGuaranteesManagerSupervisorInfo,
  fetchGuaranteesManagerTasks,
  fetchGuaranteesManagerTerms,
  fetchGuaranteesPrincipalDebitAccounts,
  fetchGuaranteesPrincipal,
  fetchGuaranteesProzorroTenderById,
  fetchGuaranteesSecurityCurrencies,
  fetchGuaranteesUdbtfTemplateDocument,
  fetchGuaranteesUakdoSupervisorInfo,
  generateGuaranteesConclusionAttachment,
  patchGuaranteesApplicationMainDeal,
  patchGuaranteesApplicationSecret,
  patchGuaranteesApplicationTender,
  patchGuaranteesApplicationAssignee,
  patchGuaranteesBaseRelations,
  patchGuaranteesBeneficiary,
  patchGuaranteesExecutionForm,
  patchGuaranteesForm,
  patchGuaranteesIssueGuarantee,
  patchGuaranteesMainDealConditions,
  patchGuaranteesManagerTerms,
  patchGuaranteesPrincipal,
  patchGuaranteesRootApplicationSection,
  putGuaranteesSecurityCurrencies,
  rejectGuaranteesApplicationByUakdoController,
  submitGuaranteesApprovalLetterByAmManager,
  submitGuaranteesApprovalLetterByClient,
  submitGuaranteesApprovalLetterByUdbtf,
  submitGuaranteesApprovalLetterByUsapok,
  submitGuaranteesApplicationByAmManager,
  submitGuaranteesApplicationByClient,
  submitGuaranteesApplicationByManager,
  submitGuaranteesApplicationByUdbtf,
  submitGuaranteesApplicationByUakdo,
  submitGuaranteesApplicationByUakdoController,
  updateGuaranteesApplicationStatus,
  updateGuaranteesUmvnApprovalStatus,
  updateGuaranteesUdbtfApprovalStatus,
  updateGuaranteesUoApprovalStatus,
} from './thunks/applicationThunks.ts';
import {
  fetchGuaranteesSupervisorSegments,
  fetchGuaranteesSupervisorSegmentsByGroup,
} from './thunks/supervisorSegmentThunks.ts';
import {
  deleteGuaranteesApplicationFile,
  downloadGuaranteesApplicationFile,
  fetchGuaranteesApplicationFileCapabilities,
  fetchGuaranteesApplicationFileTypeCapabilities,
  patchGuaranteesApplicationFileCapabilities,
  regenerateGuaranteesApplicationFile,
  uploadGuaranteesApplicationFile,
} from './thunks/applicationFileThunks.ts';
import { saveFilesSignedDocuments } from './thunks/filesSigningThunks.ts';
import {
    getCurrentApplicationAfterSelectionPatch,
  isGuaranteesApplicationPayload,
  syncCurrentApplicationInternalApprovalStatus,
  updateCurrentGuaranteesApplicationVersion,
  updateGuaranteesApplicationInTasks,
} from './utils.ts';

const guaranteesPageSlice = createSlice({
  name: 'guaranteesPage',
  initialState,
  reducers: {
    resetGuaranteesApplications: resetGuaranteesApplicationsAction,
  },
  extraReducers: (builder) => {
    builder
      .addCase(clearUsersState, resetSessionScopedGuaranteesState)
      .addCase(logout.fulfilled, resetSessionScopedGuaranteesState)
      .addCase(login.fulfilled, resetSessionScopedGuaranteesState)
      .addCase(loginManager.fulfilled, resetSessionScopedGuaranteesState);

    builder
      .addCase(fetchGuaranteesApplications.pending, (state) =>
        setGuaranteesPendingAction(state.applications)
      )
      .addCase(fetchGuaranteesApplications.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.applications, payload)
      )
      .addCase(fetchGuaranteesApplications.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.applications, payload)
      )
      .addCase(fetchGuaranteesManagerSupervisorInfo.pending, (state) => {
        setGuaranteesPendingAction(state.managerSupervisorInfo);
        state.managerSupervisorInfo.data = null;
      })
      .addCase(fetchGuaranteesManagerSupervisorInfo.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.managerSupervisorInfo, payload)
      )
      .addCase(fetchGuaranteesManagerSupervisorInfo.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.managerSupervisorInfo, payload);
        state.managerSupervisorInfo.data = null;
      })
      .addCase(fetchGuaranteesUakdoSupervisorInfo.pending, (state) => {
        setGuaranteesPendingAction(state.uakdoSupervisorInfo);
        state.uakdoSupervisorInfo.data = null;
      })
      .addCase(fetchGuaranteesUakdoSupervisorInfo.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.uakdoSupervisorInfo, payload)
      )
      .addCase(fetchGuaranteesUakdoSupervisorInfo.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.uakdoSupervisorInfo, payload);
        state.uakdoSupervisorInfo.data = null;
      })
      .addCase(fetchGuaranteesAdministrationSupervisorInfo.pending, (state, { meta }) => {
        const pageState = state.administrationSupervisorInfoByPage[meta.arg.pageKey];

        setGuaranteesPendingAction(pageState);
        pageState.data = null;
      })
      .addCase(fetchGuaranteesAdministrationSupervisorInfo.fulfilled, (state, { meta, payload }) =>
        setGuaranteesFulfilledAction(
          state.administrationSupervisorInfoByPage[meta.arg.pageKey],
          payload
        )
      )
      .addCase(fetchGuaranteesAdministrationSupervisorInfo.rejected, (state, { meta, payload }) => {
        const pageState = state.administrationSupervisorInfoByPage[meta.arg.pageKey];

        setGuaranteesRejectedAction(pageState, payload);
        pageState.data = null;
      })
      .addCase(fetchGuaranteesManagerTasks.pending, (state) => {
        setGuaranteesPendingAction(state.managerTasks);
        state.managerTasks.data.tasks = [];
      })
      .addCase(fetchGuaranteesManagerTasks.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.managerTasks, payload)
      )
      .addCase(fetchGuaranteesManagerTasks.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.managerTasks, payload);
        state.managerTasks.data = initialState.managerTasks.data;
      })
      .addCase(fetchGuaranteesAdministrationRoles.pending, (state) => {
        setGuaranteesPendingAction(state.administrationRoles);
        state.administrationRoles.data = initialState.administrationRoles.data;
      })
      .addCase(fetchGuaranteesAdministrationRoles.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.administrationRoles, payload)
      )
      .addCase(fetchGuaranteesAdministrationRoles.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.administrationRoles, payload);
        state.administrationRoles.data = initialState.administrationRoles.data;
      })
      .addCase(fetchGuaranteesAdministrationTasks.pending, (state) => {
        setGuaranteesPendingAction(state.administrationTasks);
        state.administrationTasks.data.tasks = [];
      })
      .addCase(fetchGuaranteesAdministrationTasks.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.administrationTasks, payload)
      )
      .addCase(fetchGuaranteesAdministrationTasks.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.administrationTasks, payload);
        state.administrationTasks.data = initialState.administrationTasks.data;
      })
      .addCase(updateGuaranteesApplicationStatus.pending, (state) =>
        setGuaranteesPendingAction(state.updateApplicationStatus)
      )
      .addCase(updateGuaranteesApplicationStatus.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.updateApplicationStatus, payload);

        const currentApplication = state.currentApplication.data;
        const isCurrentApplication =
          currentApplication &&
          (currentApplication.id === payload.id || currentApplication.applicationId === payload.id);

        if (isCurrentApplication) {
          Object.assign(currentApplication, payload);
        }
      })
      .addCase(updateGuaranteesApplicationStatus.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.updateApplicationStatus, payload)
      )
      .addCase(fetchGuaranteesTaskSupervisors.pending, (state) => {
        setGuaranteesPendingAction(state.taskSupervisors);
        state.taskSupervisors.data = null;
      })
      .addCase(fetchGuaranteesTaskSupervisors.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.taskSupervisors, payload)
      )
      .addCase(fetchGuaranteesTaskSupervisors.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.taskSupervisors, payload);
        state.taskSupervisors.data = null;
      })
      .addCase(patchGuaranteesApplicationAssignee.pending, (state) =>
        setGuaranteesPendingAction(state.patchApplicationAssignee)
      )
      .addCase(patchGuaranteesApplicationAssignee.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.patchApplicationAssignee, payload);
        updateGuaranteesApplicationInTasks(state.administrationTasks.data.tasks, payload);
      })
      .addCase(patchGuaranteesApplicationAssignee.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchApplicationAssignee, payload)
      )
      .addCase(updateGuaranteesUdbtfApprovalStatus.pending, (state) =>
        setGuaranteesPendingAction(state.updateUdbtfApprovalStatus)
      )
      .addCase(updateGuaranteesUdbtfApprovalStatus.fulfilled, (state, { meta, payload }) => {
        setGuaranteesFulfilledAction(state.updateUdbtfApprovalStatus, payload);
        syncCurrentApplicationInternalApprovalStatus(
          state,
          payload,
          meta.arg.applicationId,
          'udbtfApprovalStatus',
          meta.arg.applicationApprovalStatus
        );
      })
      .addCase(updateGuaranteesUdbtfApprovalStatus.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.updateUdbtfApprovalStatus, payload)
      )
      .addCase(updateGuaranteesUoApprovalStatus.pending, (state) =>
        setGuaranteesPendingAction(state.updateUoApprovalStatus)
      )
      .addCase(updateGuaranteesUoApprovalStatus.fulfilled, (state, { meta, payload }) => {
        setGuaranteesFulfilledAction(state.updateUoApprovalStatus, payload);
        syncCurrentApplicationInternalApprovalStatus(
          state,
          payload,
          meta.arg.applicationId,
          'uoApprovalStatus',
          meta.arg.applicationApprovalStatus
        );
      })
      .addCase(updateGuaranteesUoApprovalStatus.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.updateUoApprovalStatus, payload)
      )
      .addCase(updateGuaranteesUmvnApprovalStatus.pending, (state) =>
        setGuaranteesPendingAction(state.updateUmvnApprovalStatus)
      )
      .addCase(updateGuaranteesUmvnApprovalStatus.fulfilled, (state, { meta, payload }) => {
        setGuaranteesFulfilledAction(state.updateUmvnApprovalStatus, payload);
        syncCurrentApplicationInternalApprovalStatus(
          state,
          payload,
          meta.arg.applicationId,
          'umvnApprovalStatus',
          meta.arg.applicationApprovalStatus
        );
      })
      .addCase(updateGuaranteesUmvnApprovalStatus.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.updateUmvnApprovalStatus, payload)
      )
      .addCase(submitGuaranteesApplicationByClient.pending, (state) =>
        setGuaranteesPendingAction(state.submitApplicationByClient)
      )
      .addCase(submitGuaranteesApplicationByClient.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.submitApplicationByClient, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(submitGuaranteesApplicationByClient.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.submitApplicationByClient, payload)
      )
      .addCase(submitGuaranteesApprovalLetterByClient.pending, (state) =>
        setGuaranteesPendingAction(state.submitApprovalLetterByClient)
      )
      .addCase(submitGuaranteesApprovalLetterByClient.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.submitApprovalLetterByClient, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(submitGuaranteesApprovalLetterByClient.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.submitApprovalLetterByClient, payload)
      )
      .addCase(submitGuaranteesApplicationByManager.pending, (state) =>
        setGuaranteesPendingAction(state.submitApplicationByManager)
      )
      .addCase(submitGuaranteesApplicationByManager.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.submitApplicationByManager, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(submitGuaranteesApplicationByManager.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.submitApplicationByManager, payload)
      )
      .addCase(submitGuaranteesApplicationByUdbtf.pending, (state) =>
        setGuaranteesPendingAction(state.submitApplicationByUdbtf)
      )
      .addCase(submitGuaranteesApplicationByUdbtf.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.submitApplicationByUdbtf, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(submitGuaranteesApplicationByUdbtf.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.submitApplicationByUdbtf, payload)
      )
      .addCase(submitGuaranteesApprovalLetterByUdbtf.pending, (state) =>
        setGuaranteesPendingAction(state.submitApprovalLetterByUdbtf)
      )
      .addCase(submitGuaranteesApprovalLetterByUdbtf.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.submitApprovalLetterByUdbtf, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(submitGuaranteesApprovalLetterByUdbtf.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.submitApprovalLetterByUdbtf, payload)
      )
      .addCase(submitGuaranteesApprovalLetterByUsapok.pending, (state) =>
        setGuaranteesPendingAction(state.submitApprovalLetterByUsapok)
      )
      .addCase(submitGuaranteesApprovalLetterByUsapok.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.submitApprovalLetterByUsapok, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(submitGuaranteesApprovalLetterByUsapok.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.submitApprovalLetterByUsapok, payload)
      )
      .addCase(submitGuaranteesApplicationByUakdo.pending, (state) =>
        setGuaranteesPendingAction(state.submitApplicationByUakdo)
      )
      .addCase(submitGuaranteesApplicationByUakdo.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.submitApplicationByUakdo, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(submitGuaranteesApplicationByUakdo.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.submitApplicationByUakdo, payload)
      )
      .addCase(submitGuaranteesApplicationByUakdoController.pending, (state) =>
        setGuaranteesPendingAction(state.submitApplicationByUakdoController)
      )
      .addCase(submitGuaranteesApplicationByUakdoController.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.submitApplicationByUakdoController, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(submitGuaranteesApplicationByUakdoController.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.submitApplicationByUakdoController, payload)
      )
      .addCase(rejectGuaranteesApplicationByUakdoController.pending, (state) =>
        setGuaranteesPendingAction(state.rejectApplicationByUakdoController)
      )
      .addCase(rejectGuaranteesApplicationByUakdoController.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.rejectApplicationByUakdoController, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(rejectGuaranteesApplicationByUakdoController.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.rejectApplicationByUakdoController, payload)
      )
      .addCase(submitGuaranteesApplicationByAmManager.pending, (state) =>
        setGuaranteesPendingAction(state.submitApplicationByAmManager)
      )
      .addCase(submitGuaranteesApplicationByAmManager.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.submitApplicationByAmManager, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(submitGuaranteesApplicationByAmManager.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.submitApplicationByAmManager, payload)
      )
      .addCase(submitGuaranteesApprovalLetterByAmManager.pending, (state) =>
        setGuaranteesPendingAction(state.submitApprovalLetterByAmManager)
      )
      .addCase(submitGuaranteesApprovalLetterByAmManager.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.submitApprovalLetterByAmManager, payload);

        if (isGuaranteesApplicationPayload(payload)) {
          setGuaranteesFulfilledAction(state.currentApplication, payload);
        } else {
          updateCurrentGuaranteesApplicationVersion(state, payload);
        }
      })
      .addCase(submitGuaranteesApprovalLetterByAmManager.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.submitApprovalLetterByAmManager, payload)
      )
      .addCase(saveFilesSignedDocuments.fulfilled, (state, { payload }) => {
        updateCurrentGuaranteesApplicationVersion(state, payload);
      })
      .addCase(fetchGuaranteesApplicationTimeline.pending, (state) => {
        setGuaranteesPendingAction(state.applicationTimeline);
        state.applicationTimeline.data = [];
      })
      .addCase(fetchGuaranteesApplicationTimeline.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.applicationTimeline, payload)
      )
      .addCase(fetchGuaranteesApplicationTimeline.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.applicationTimeline, payload);
        state.applicationTimeline.data = [];
      })
      .addCase(fetchGuaranteesApplicationFileCapabilities.pending, (state) => {
        setGuaranteesPendingAction(state.applicationFileCapabilities);
        state.applicationFileCapabilities.data = { fileTypes: [] };
      })
      .addCase(fetchGuaranteesApplicationFileCapabilities.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.applicationFileCapabilities, payload)
      )
      .addCase(fetchGuaranteesApplicationFileCapabilities.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.applicationFileCapabilities, payload);
        state.applicationFileCapabilities.data = { fileTypes: [] };
      })
      .addCase(fetchGuaranteesApplicationFileTypeCapabilities.pending, (state) => {
        setGuaranteesPendingAction(state.applicationFileTypeCapabilities);
        state.applicationFileTypeCapabilities.data = { fileTypes: [] };
      })
      .addCase(fetchGuaranteesApplicationFileTypeCapabilities.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.applicationFileTypeCapabilities, payload)
      )
      .addCase(fetchGuaranteesApplicationFileTypeCapabilities.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.applicationFileTypeCapabilities, payload);
        state.applicationFileTypeCapabilities.data = { fileTypes: [] };
      })
      .addCase(uploadGuaranteesApplicationFile.pending, (state) => {
        setGuaranteesPendingAction(state.uploadApplicationFile);
        state.uploadApplicationFile.data = null;
      })
      .addCase(uploadGuaranteesApplicationFile.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.uploadApplicationFile, payload)
      )
      .addCase(uploadGuaranteesApplicationFile.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.uploadApplicationFile, payload);
        state.uploadApplicationFile.data = null;
      })
      .addCase(regenerateGuaranteesApplicationFile.pending, (state) => {
        setGuaranteesPendingAction(state.regenerateApplicationFile);
        state.regenerateApplicationFile.data = null;
      })
      .addCase(regenerateGuaranteesApplicationFile.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.regenerateApplicationFile, payload)
      )
      .addCase(regenerateGuaranteesApplicationFile.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.regenerateApplicationFile, payload);
        state.regenerateApplicationFile.data = null;
      })
      .addCase(patchGuaranteesApplicationFileCapabilities.pending, (state) => {
        setGuaranteesPendingAction(state.patchApplicationFileCapabilities);
        state.patchApplicationFileCapabilities.data = null;
      })
      .addCase(patchGuaranteesApplicationFileCapabilities.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.patchApplicationFileCapabilities, payload);
        state.applicationFileTypeCapabilities.data = {
          fileTypes: state.applicationFileTypeCapabilities.data.fileTypes.map((capability) =>
            capability.fileType === payload.fileType ? payload : capability
          ),
        };
      })
      .addCase(patchGuaranteesApplicationFileCapabilities.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.patchApplicationFileCapabilities, payload);
        state.patchApplicationFileCapabilities.data = null;
      })
      .addCase(downloadGuaranteesApplicationFile.pending, (state) => {
        setGuaranteesPendingAction(state.downloadApplicationFile);
        state.downloadApplicationFile.data = null;
      })
      .addCase(downloadGuaranteesApplicationFile.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.downloadApplicationFile, payload)
      )
      .addCase(downloadGuaranteesApplicationFile.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.downloadApplicationFile, payload);
        state.downloadApplicationFile.data = null;
      })
      .addCase(deleteGuaranteesApplicationFile.pending, (state) => {
        setGuaranteesPendingAction(state.deleteApplicationFile);
        state.deleteApplicationFile.data = null;
      })
      .addCase(deleteGuaranteesApplicationFile.fulfilled, (state) =>
        setGuaranteesFulfilledAction(state.deleteApplicationFile, null)
      )
      .addCase(deleteGuaranteesApplicationFile.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.deleteApplicationFile, payload);
        state.deleteApplicationFile.data = null;
      })
      .addCase(fetchGuaranteesApplication.pending, handleFetchGuaranteesApplicationPending)
      .addCase(fetchGuaranteesApplication.fulfilled, (state, { payload }) =>
        handleFetchGuaranteesApplicationFulfilled(state, payload)
      )
      .addCase(fetchGuaranteesApplication.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.currentApplication, payload);
        state.currentApplication.data = null;
      })
      .addCase(fetchGuaranteesUdbtfTemplateDocument.pending, (state) => {
        setGuaranteesPendingAction(state.udbtfTemplateDocument);
        state.udbtfTemplateDocument.data = null;
      })
      .addCase(fetchGuaranteesUdbtfTemplateDocument.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.udbtfTemplateDocument, payload)
      )
      .addCase(fetchGuaranteesUdbtfTemplateDocument.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.udbtfTemplateDocument, payload);
        state.udbtfTemplateDocument.data = null;
      })
      .addCase(fetchGuaranteesAmTemplateDocument.pending, (state) => {
        setGuaranteesPendingAction(state.amTemplateDocument);
        state.amTemplateDocument.data = null;
      })
      .addCase(fetchGuaranteesAmTemplateDocument.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.amTemplateDocument, payload)
      )
      .addCase(fetchGuaranteesAmTemplateDocument.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.amTemplateDocument, payload);
        state.amTemplateDocument.data = null;
      })
      .addCase(fetchGuaranteesConclusionTemplateDocument.pending, (state) => {
        setGuaranteesPendingAction(state.conclusionTemplateDocument);
        state.conclusionTemplateDocument.data = null;
      })
      .addCase(fetchGuaranteesConclusionTemplateDocument.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.conclusionTemplateDocument, payload)
      )
      .addCase(fetchGuaranteesConclusionTemplateDocument.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.conclusionTemplateDocument, payload);
        state.conclusionTemplateDocument.data = null;
      })
      .addCase(fetchGuaranteesApprovalComment.pending, (state) => {
        setGuaranteesPendingAction(state.approvalComment);
        state.approvalComment.data = null;
      })
      .addCase(fetchGuaranteesApprovalComment.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.approvalComment, payload)
      )
      .addCase(fetchGuaranteesApprovalComment.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.approvalComment, payload);
        state.approvalComment.data = null;
      })
      .addCase(generateGuaranteesConclusionAttachment.pending, (state) => {
        setGuaranteesPendingAction(state.generateConclusionAttachment);
        state.generateConclusionAttachment.data = null;
      })
      .addCase(generateGuaranteesConclusionAttachment.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.generateConclusionAttachment, payload)
      )
      .addCase(generateGuaranteesConclusionAttachment.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.generateConclusionAttachment, payload);
        state.generateConclusionAttachment.data = null;
      })
      .addCase(patchGuaranteesRootApplicationSection.pending, (state) =>
        setGuaranteesPendingAction(state.patchRootApplicationSection)
      )
      .addCase(patchGuaranteesRootApplicationSection.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.patchRootApplicationSection, payload);
        setGuaranteesFulfilledAction(state.currentApplication, payload);
      })
      .addCase(patchGuaranteesRootApplicationSection.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchRootApplicationSection, payload)
      )
      .addCase(fetchGuaranteesPrincipal.pending, (state) => {
        setGuaranteesPendingAction(state.principal);
        state.principal.data = null;
      })
      .addCase(fetchGuaranteesPrincipal.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.principal, payload);
      })
      .addCase(fetchGuaranteesPrincipal.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.principal, payload);
        state.principal.data = null;
      })
      .addCase(patchGuaranteesPrincipal.pending, (state) =>
        setGuaranteesPendingAction(state.patchPrincipal)
      )
      .addCase(patchGuaranteesPrincipal.fulfilled, (state, { payload }) =>
        handlePatchGuaranteesPrincipalFulfilled(state, payload)
      )
      .addCase(patchGuaranteesPrincipal.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchPrincipal, payload)
      )
      .addCase(fetchGuaranteesBeneficiary.pending, (state) => {
        setGuaranteesPendingAction(state.beneficiary);
        state.beneficiary.data = [];
      })
      .addCase(fetchGuaranteesBeneficiary.fulfilled, (state, { payload }) => {
        synchronizeGuaranteesBeneficiaryResponse(state, payload);
      })
      .addCase(fetchGuaranteesBeneficiary.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.beneficiary, payload);
        state.beneficiary.data = [];
      })
      .addCase(patchGuaranteesBeneficiary.pending, (state) =>
        setGuaranteesPendingAction(state.patchBeneficiary)
      )
      .addCase(patchGuaranteesBeneficiary.fulfilled, (state, { payload }) =>
        handlePatchGuaranteesBeneficiaryFulfilled(state, payload)
      )
      .addCase(patchGuaranteesBeneficiary.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchBeneficiary, payload)
      )
      .addCase(fetchGuaranteesBaseRelations.pending, (state) => {
        setGuaranteesPendingAction(state.baseRelations);
        state.baseRelations.data = null;
      })
      .addCase(fetchGuaranteesBaseRelations.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.baseRelations, payload);

        if (state.currentApplication.data && payload) {
          state.currentApplication.data.baseRelations = payload;
        }
      })
      .addCase(fetchGuaranteesBaseRelations.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.baseRelations, payload);
        state.baseRelations.data = null;
      })
      .addCase(patchGuaranteesBaseRelations.pending, (state) =>
        setGuaranteesPendingAction(state.patchBaseRelations)
      )
      .addCase(patchGuaranteesBaseRelations.fulfilled, (state, { payload }) =>
        handlePatchGuaranteesBaseRelationsFulfilled(state, payload)
      )
      .addCase(patchGuaranteesBaseRelations.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchBaseRelations, payload)
      )
      .addCase(fetchGuaranteesManagerTerms.pending, (state) => {
        setGuaranteesPendingAction(state.managerTerms);
        state.managerTerms.data = null;
      })
      .addCase(fetchGuaranteesManagerTerms.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.managerTerms, payload);

        if (state.currentApplication.data) {
          state.currentApplication.data.managerTerms = payload;
        }
      })
      .addCase(fetchGuaranteesManagerTerms.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.managerTerms, payload);
        state.managerTerms.data = null;
      })
      .addCase(patchGuaranteesManagerTerms.pending, (state) =>
        setGuaranteesPendingAction(state.patchManagerTerms)
      )
      .addCase(patchGuaranteesManagerTerms.fulfilled, (state, { payload }) =>
        handlePatchGuaranteesManagerTermsFulfilled(state, payload)
      )
      .addCase(patchGuaranteesManagerTerms.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchManagerTerms, payload)
      )
      .addCase(patchGuaranteesExecutionForm.pending, (state) =>
        setGuaranteesPendingAction(state.patchExecutionForm)
      )
      .addCase(patchGuaranteesExecutionForm.fulfilled, (state, { payload }) =>
        handlePatchGuaranteesExecutionFormFulfilled(state, payload)
      )
      .addCase(patchGuaranteesExecutionForm.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchExecutionForm, payload)
      )
      .addCase(patchGuaranteesIssueGuarantee.pending, (state) =>
        setGuaranteesPendingAction(state.patchIssueGuarantee)
      )
      .addCase(patchGuaranteesIssueGuarantee.fulfilled, (state, { payload }) =>
        handlePatchGuaranteesIssueGuaranteeFulfilled(state, payload)
      )
      .addCase(patchGuaranteesIssueGuarantee.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchIssueGuarantee, payload)
      )
      .addCase(fetchGuaranteesExecutionForm.pending, (state) => {
        setGuaranteesPendingAction(state.executionForm);
        state.executionForm.data = null;
      })
      .addCase(fetchGuaranteesExecutionForm.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.executionForm, payload);

        if (state.currentApplication.data) {
          state.currentApplication.data.executionForm = payload;
        }
      })
      .addCase(fetchGuaranteesExecutionForm.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.executionForm, payload);
        state.executionForm.data = null;
      })
      .addCase(fetchGuaranteesForm.pending, (state) => {
        setGuaranteesPendingAction(state.guaranteeForm);
        state.guaranteeForm.data = null;
      })
      .addCase(fetchGuaranteesForm.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.guaranteeForm, payload);

        if (state.currentApplication.data) {
          state.currentApplication.data.guaranteeForm = payload;
        }
      })
      .addCase(fetchGuaranteesForm.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.guaranteeForm, payload);
        state.guaranteeForm.data = null;
      })
      .addCase(patchGuaranteesForm.pending, (state) =>
        setGuaranteesPendingAction(state.patchGuaranteeForm)
      )
      .addCase(patchGuaranteesForm.fulfilled, (state, { payload }) =>
        handlePatchGuaranteesFormFulfilled(state, payload)
      )
      .addCase(patchGuaranteesForm.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchGuaranteeForm, payload)
      )
      .addCase(fetchGuaranteesMainDealConditions.pending, (state) => {
        setGuaranteesPendingAction(state.mainDealConditions);
        state.mainDealConditions.data = null;
      })
      .addCase(fetchGuaranteesMainDealConditions.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.mainDealConditions, payload);

        if (state.currentApplication.data) {
          state.currentApplication.data.mainDealConditions = payload;
        }
      })
      .addCase(fetchGuaranteesMainDealConditions.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.mainDealConditions, payload);
        state.mainDealConditions.data = null;
      })
      .addCase(patchGuaranteesMainDealConditions.pending, (state) =>
        setGuaranteesPendingAction(state.patchMainDealConditions)
      )
      .addCase(patchGuaranteesMainDealConditions.fulfilled, (state, { payload }) =>
        handlePatchGuaranteesMainDealConditionsFulfilled(state, payload)
      )
      .addCase(patchGuaranteesMainDealConditions.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchMainDealConditions, payload)
      )
      .addCase(patchGuaranteesApplicationTender.pending, (state) =>
        setGuaranteesPendingAction(state.patchApplicationTender)
      )
      .addCase(patchGuaranteesApplicationTender.fulfilled, (state, { meta, payload }) => {
        setGuaranteesFulfilledAction(state.patchApplicationTender, payload.application);
        const currentApplication = getCurrentApplicationAfterSelectionPatch(
          state.currentApplication.data,
          payload
        );

        if (currentApplication) {
          setGuaranteesFulfilledAction(state.currentApplication, currentApplication);
        }

        setGuaranteesFulfilledAction(state.applicationTenderInfo, {
          tender: {
            lots: meta.arg.selectedLotIds
              .map((lotId) => lotId.trim())
              .filter(Boolean)
              .map((id) => ({ id })),
          },
        });
      })
      .addCase(patchGuaranteesApplicationTender.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchApplicationTender, payload)
      )
      .addCase(fetchGuaranteesApplicationTenderInfo.pending, (state) => {
        setGuaranteesPendingAction(state.applicationTenderInfo);
        state.applicationTenderInfo.data = null;
      })
      .addCase(fetchGuaranteesApplicationTenderInfo.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.applicationTenderInfo, payload);
      })
      .addCase(fetchGuaranteesApplicationTenderInfo.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.applicationTenderInfo, payload);
        state.applicationTenderInfo.data = null;
      })
      .addCase(fetchGuaranteesProzorroTenderById.pending, (state) => {
        setGuaranteesPendingAction(state.fetchProzorroTender);
        state.fetchProzorroTender.data = null;
      })
      .addCase(fetchGuaranteesProzorroTenderById.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.fetchProzorroTender, payload);
      })
      .addCase(fetchGuaranteesProzorroTenderById.rejected, (state, { payload }) => {
        setGuaranteesRejectedAction(state.fetchProzorroTender, payload);
        state.fetchProzorroTender.data = null;
      })
      .addCase(fetchGuaranteesMainDeals.pending, (state, { meta }) =>
        handleFetchGuaranteesMainDealsPending(state, meta.arg)
      )
      .addCase(fetchGuaranteesMainDeals.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.mainDeals, payload);
      })
      .addCase(fetchGuaranteesMainDeals.rejected, (state, { payload }) =>
        handleFetchGuaranteesMainDealsRejected(state, payload)
      )
      .addCase(patchGuaranteesApplicationMainDeal.pending, (state) =>
        setGuaranteesPendingAction(state.patchApplicationMainDeal)
      )
      .addCase(patchGuaranteesApplicationMainDeal.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.patchApplicationMainDeal, payload.application);
        const currentApplication = getCurrentApplicationAfterSelectionPatch(
          state.currentApplication.data,
          payload
        );

        if (currentApplication) {
          setGuaranteesFulfilledAction(state.currentApplication, currentApplication);
        }
      })
      .addCase(patchGuaranteesApplicationMainDeal.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchApplicationMainDeal, payload)
      )
      .addCase(fetchGuaranteesAccounts.pending, handleFetchGuaranteesAccountsPending)
      .addCase(fetchGuaranteesAccounts.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.accounts, payload);
      })
      .addCase(fetchGuaranteesAccounts.rejected, (state, { payload }) =>
        handleFetchGuaranteesAccountsRejected(state, payload)
      )
      .addCase(fetchGuaranteesPrincipalDebitAccounts.pending, (state) =>
        setGuaranteesPendingAction(state.principalDebitAccounts)
      )
      .addCase(fetchGuaranteesPrincipalDebitAccounts.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.principalDebitAccounts, payload);
      })
      .addCase(fetchGuaranteesPrincipalDebitAccounts.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.principalDebitAccounts, payload)
      )
      .addCase(patchGuaranteesApplicationSecret.pending, (state) =>
        setGuaranteesPendingAction(state.patchApplicationSecret)
      )
      .addCase(patchGuaranteesApplicationSecret.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.patchApplicationSecret, payload);
        setGuaranteesFulfilledAction(state.currentApplication, payload);
      })
      .addCase(patchGuaranteesApplicationSecret.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.patchApplicationSecret, payload)
      )
      .addCase(fetchGuaranteesSecurityCurrencies.pending, (state) =>
        setGuaranteesPendingAction(state.securityCurrencies)
      )
      .addCase(fetchGuaranteesSecurityCurrencies.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.securityCurrencies, payload);

        if (state.currentApplication.data?.security) {
          state.currentApplication.data.security.currencies = payload.currencies;
        }
      })
      .addCase(fetchGuaranteesSecurityCurrencies.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.securityCurrencies, payload)
      )
      .addCase(putGuaranteesSecurityCurrencies.pending, (state) =>
        setGuaranteesPendingAction(state.putSecurityCurrencies)
      )
      .addCase(putGuaranteesSecurityCurrencies.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.putSecurityCurrencies, payload);
        setGuaranteesFulfilledAction(state.currentApplication, payload);
        setGuaranteesFulfilledAction(state.securityCurrencies, {
          currencies: Array.isArray(payload.security?.currencies)
            ? payload.security.currencies
            : [],
        });
      })
      .addCase(putGuaranteesSecurityCurrencies.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.putSecurityCurrencies, payload)
      )
      .addCase(checkGuaranteesApplicationRequest.pending, (state) =>
        setGuaranteesPendingAction(state.checkApplicationRequest)
      )
      .addCase(checkGuaranteesApplicationRequest.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.checkApplicationRequest, payload);
        setGuaranteesFulfilledAction(state.currentApplication, payload);
      })
      .addCase(checkGuaranteesApplicationRequest.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.checkApplicationRequest, payload)
      )
      .addCase(createGuaranteesApplication.pending, (state) =>
        setGuaranteesPendingAction(state.createApplication)
      )
      .addCase(createGuaranteesApplication.fulfilled, (state, { payload }) => {
        setGuaranteesFulfilledAction(state.createApplication, payload);
        setGuaranteesFulfilledAction(state.currentApplication, payload);
      })
      .addCase(createGuaranteesApplication.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.createApplication, payload)
      )
      .addCase(fetchGuaranteesSupervisorSegments.pending, (state) =>
        setGuaranteesPendingAction(state.supervisorSegments)
      )
      .addCase(fetchGuaranteesSupervisorSegments.fulfilled, (state, { payload }) =>
        setGuaranteesFulfilledAction(state.supervisorSegments, payload)
      )
      .addCase(fetchGuaranteesSupervisorSegments.rejected, (state, { payload }) =>
        setGuaranteesRejectedAction(state.supervisorSegments, payload)
      )
      .addCase(fetchGuaranteesSupervisorSegmentsByGroup.pending, (state, { meta }) => {
        const groupState = state.supervisorSegmentsByGroup[meta.arg.groupKey];

        setGuaranteesPendingAction(groupState);
        groupState.data = [];
      })
      .addCase(fetchGuaranteesSupervisorSegmentsByGroup.fulfilled, (state, { meta, payload }) =>
        setGuaranteesFulfilledAction(state.supervisorSegmentsByGroup[meta.arg.groupKey], payload)
      )
      .addCase(fetchGuaranteesSupervisorSegmentsByGroup.rejected, (state, { meta, payload }) =>
        setGuaranteesRejectedAction(state.supervisorSegmentsByGroup[meta.arg.groupKey], payload)
      );
  },
});
export default guaranteesPageSlice.reducer;
